import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Slider, Radio, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import cropSvg from "../../../../assets/icons/crop.svg";
import zoomInSvg from "../../../../assets/icons/zoom_in.svg";
import "./CustomImageCrop.scss";

interface CustomImageCropProps {
  visible: boolean;
  onCancel: () => void;
  onApply: (croppedImage: string) => void;
  imageUrl: string;
  title: string;
  isMobile?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const CustomImageCrop: React.FC<CustomImageCropProps> = ({
  visible,
  onCancel,
  onApply,
  imageUrl,
  title,
  isMobile = false,
  onLoadingChange,
}) => {
  const [cropArea, setCropArea] = useState<CropArea>({
    x: 0,
    y: 0,
    width: 200,
    height: 200,
  });
  const [zoom, setZoom] = useState(1);
  const [aspectRatio, setAspectRatio] = useState<string>("16:9");
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const aspectRatios = {
    "16:9": 16 / 5,
    "4:3": 4 / 3,
    "1:1": 1,
    "9:16": 9 / 17, // Mobile portrait ratio
    // "Custom": null, // Commented out custom cropping
  };

  // Desktop: landscape ratio (16:9), Mobile: portrait ratio (9:16)
  const defaultAspectRatio = isMobile ? "9:16" : "16:9";

  useEffect(() => {
    setAspectRatio(defaultAspectRatio);
  }, [isMobile]);

  useEffect(() => {
    if (visible && imageUrl) {
      // Notify parent component that loading has started
      onLoadingChange?.(true);

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (imageRef.current) {
          imageRef.current.src = imageUrl;
          setImageDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          // Delay initialization to ensure container is rendered
          setTimeout(() => {
            initializeCropArea();
            // Notify parent component that loading has finished
            onLoadingChange?.(false);
          }, 100);
        }
      };
      img.onerror = () => {
        onLoadingChange?.(false);
      };
      img.src = imageUrl;
    }
  }, [visible, imageUrl, onLoadingChange]);

  useEffect(() => {
    // if (aspectRatio !== "Custom") { // Commented out custom check
    const ratio = aspectRatios[aspectRatio as keyof typeof aspectRatios];
    if (ratio) {
      setCropArea((prev) => ({
        ...prev,
        height: prev.width / ratio,
      }));
    }
    // }
  }, [aspectRatio]);

  //   const initializeCropArea = () => {
  //     if (!containerRef.current || !imageRef.current) return;

  //     const container = containerRef.current;
  //     const containerRect = container.getBoundingClientRect();
  //     const img = imageRef.current;
  //     const imgRect = img.getBoundingClientRect();

  //     // Calculate the actual image display area within the container
  //     const imgDisplayX = imgRect.left - containerRect.left;
  //     const imgDisplayY = imgRect.top - containerRect.top;
  //     const imgDisplayWidth = imgRect.width;
  //     const imgDisplayHeight = imgRect.height;

  //     const ratio = aspectRatios[defaultAspectRatio as keyof typeof aspectRatios];

  //     // Set appropriate default sizes based on device type
  //     let width, height;

  //     if (isMobile) {
  //       // Mobile: portrait orientation, smaller width
  //       width = imgDisplayWidth * 0.7; // 70% of image width
  //       height = ratio ? width / ratio : width;

  //       // If height is too large, adjust based on height
  //       if (height > imgDisplayHeight * 0.8) {
  //         height = imgDisplayHeight * 0.8;
  //         width = ratio ? height * ratio : width;
  //       }
  //     } else {
  //       // Desktop: landscape orientation, larger width
  //       width = imgDisplayWidth * 0.85; // 85% of image width
  //       height = ratio ? width / ratio : width;

  //       // If height is too large, adjust based on height
  //       if (height > imgDisplayHeight * 0.85) {
  //         height = imgDisplayHeight * 0.85;
  //         width = ratio ? height * ratio : width;
  //       }
  //     }

  //     // Ensure crop area fits within the image display area
  //     if (width > imgDisplayWidth) {
  //       width = imgDisplayWidth * 0.95;
  //       height = ratio ? width / ratio : height;
  //     }
  //     if (height > imgDisplayHeight) {
  //       height = imgDisplayHeight * 0.95;
  //       width = ratio ? height * ratio : width;
  //     }

  //     // Center the crop area within the image display area
  //     setCropArea({
  //       x: imgDisplayX + (imgDisplayWidth - width) / 2,
  //       y: imgDisplayY + (imgDisplayHeight - height) / 2,
  //       width,
  //       height
  //     });
  //   };

  const initializeCropArea = () => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    const ratio = aspectRatios[defaultAspectRatio as keyof typeof aspectRatios];
    let width = containerWidth * 0.8;
    let height = ratio ? width / ratio : width;

    if (height > containerHeight * 0.8) {
      height = containerHeight * 0.8;
      width = ratio ? height * ratio : height;
    }

    setCropArea({
      x: (containerWidth - width) / 2,
      y: (containerHeight - height) / 2,
      width,
      height,
    });
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking on resize handles - COMPLETELY DISABLED
    // const handleSize = 15; // Increased handle size for better interaction
    // const {
    //   x: cropX,
    //   y: cropY,
    //   width: cropWidth,
    //   height: cropHeight,
    // } = cropArea;

    // Only allow custom resizing if Custom is selected - COMMENTED OUT
    // if (aspectRatio === "Custom") {
    //   // Corner handles
    //   if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   }
    //   // Edge handles for custom mode
    //   else if (
    //     x >= cropX + handleSize &&
    //     x <= cropX + cropWidth - handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + handleSize &&
    //     x <= cropX + cropWidth - handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY + handleSize &&
    //     y <= cropY + cropHeight - handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY + handleSize &&
    //     y <= cropY + cropHeight - handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   }
    // } else {
    //   // For non-custom mode, still allow corner resizing
    //   if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   }
    // }

    // Only allow dragging if clicking inside crop area (no resizing)
    const {
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight,
    } = cropArea;

    if (
      x >= cropX &&
      x <= cropX + cropWidth &&
      y >= cropY &&
      y <= cropY + cropHeight
    ) {
      setIsDragging(true);
    }

    setDragStart({ x, y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return; // Removed isResizing check

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const deltaX = x - dragStart.x;
    const deltaY = y - dragStart.y;

    if (isDragging) {
      setCropArea((prev) => {
        // Get the actual image display area
        const img = imageRef.current;
        const imgRect = img?.getBoundingClientRect();
        if (!imgRect) return prev;

        const imgDisplayX = imgRect.left - rect.left;
        const imgDisplayY = imgRect.top - rect.top;
        const imgDisplayWidth = imgRect.width;
        const imgDisplayHeight = imgRect.height;

        const maxX = imgDisplayX + imgDisplayWidth - prev.width;
        const maxY = imgDisplayY + imgDisplayHeight - prev.height;

        return {
          ...prev,
          x: Math.max(imgDisplayX, Math.min(prev.x + deltaX, maxX)),
          y: Math.max(imgDisplayY, Math.min(prev.y + deltaY, maxY)),
        };
      });
    }
    // Removed all resize logic

    setDragStart({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleReset = () => {
    setZoom(1);
    setAspectRatio(defaultAspectRatio);
    initializeCropArea();
  };

  const handleZoomChange = (value: number) => {
    setZoom(Math.max(0.5, Math.min(3, value)));
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(3, prev + 0.1));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(0.5, prev - 0.1));
  };

  const handleApply = () => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { x, y, width, height } = cropArea;

    // Get the actual displayed image dimensions and position
    const img = imageRef.current;
    const imgRect = img.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();

    if (!containerRect) return;

    // Calculate the actual image display area within the container
    const imgDisplayWidth = imgRect.width;
    const imgDisplayHeight = imgRect.height;
    const imgDisplayX = imgRect.left - containerRect.left;
    const imgDisplayY = imgRect.top - containerRect.top;

    // Calculate the scale factors between displayed image and natural image
    const scaleX = imageDimensions.width / imgDisplayWidth;
    const scaleY = imageDimensions.height / imgDisplayHeight;

    // Calculate the crop area relative to the natural image dimensions
    // The crop area coordinates are relative to the container, so we need to:
    // 1. Convert to image-relative coordinates
    // 2. Account for zoom by scaling the coordinates
    const cropX = (x - imgDisplayX) * scaleX;
    const cropY = (y - imgDisplayY) * scaleY;
    const cropWidth = width * scaleX;
    const cropHeight = height * scaleY;

    // Ensure crop coordinates are within image bounds
    const clampedCropX = Math.max(
      0,
      Math.min(cropX, imageDimensions.width - cropWidth)
    );
    const clampedCropY = Math.max(
      0,
      Math.min(cropY, imageDimensions.height - cropHeight)
    );
    const clampedCropWidth = Math.min(
      cropWidth,
      imageDimensions.width - clampedCropX
    );
    const clampedCropHeight = Math.min(
      cropHeight,
      imageDimensions.height - clampedCropY
    );

    // Set canvas size to the crop area dimensions
    canvas.width = width;
    canvas.height = height;

    // Draw the cropped portion of the image onto the canvas
    ctx.drawImage(
      imageRef.current,
      clampedCropX,
      clampedCropY,
      clampedCropWidth,
      clampedCropHeight,
      0,
      0,
      width,
      height
    );

    const croppedImageUrl = canvas.toDataURL("image/jpeg", 0.9);
    onApply(croppedImageUrl);
  };

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
      className="custom-crop-modal"
    >
      <div className="crop-container">
        <div
          ref={containerRef}
          className="image-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Crop"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "center",
              pointerEvents: "none", // Prevent image from interfering with crop interactions
            }}
          />

          <div
            className="crop-overlay"
            style={{
              left: cropArea.x,
              top: cropArea.y,
              width: cropArea.width,
              height: cropArea.height,
              border: "2px solid #1890ff", // Simple border to show crop area
              backgroundColor: "rgba(24, 144, 255, 0.1)", // Subtle background
            }}
          >
            {/* Removed all crop handles - only visual crop area remains */}
          </div>
        </div>

        <div className="crop-controls">
          <div className="control-section crop-ratio-section">
            <div className="control-header">
              <img src={cropSvg} alt="Crop" className="control-icon" />
              <label>Cropping ratio</label>
            </div>
            <div className="control-separator"></div>
            <Radio.Group
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value)}
            >
              <Space direction="horizontal">
                {!isMobile && <Radio.Button value="16:9">16:9</Radio.Button>}
                {/* {!isMobile && <Radio.Button value="4:3">4:3</Radio.Button>}
                {!isMobile && <Radio.Button value="3:2">3:2</Radio.Button>}
                <Radio.Button value="1:1">1:1</Radio.Button> */}
                {isMobile && <Radio.Button value="9:16">9:16</Radio.Button>}
                {/* {isMobile && <Radio.Button value="4:5">4:5</Radio.Button>} */}
                {/* <Radio.Button value="Custom">Custom</Radio.Button> */}
              </Space>
            </Radio.Group>
          </div>

          <div className="control-section zoom-section">
            <div className="control-header">
              <img src={zoomInSvg} alt="Zoom" className="control-icon" />
              <label>Zoom</label>
            </div>
            <div className="control-separator"></div>
            <div className="zoom-controls">
              <span className="zoom-minus" onClick={handleZoomOut}>
                -
              </span>
              <Slider
                min={0.5}
                max={3}
                step={0.1}
                value={zoom}
                onChange={handleZoomChange}
                className="zoom-slider"
              />
              <span className="zoom-plus" onClick={handleZoomIn}>
                +
              </span>
            </div>
          </div>
        </div>

        <div className="crop-actions">
          <Button onClick={handleReset}>Reset</Button>
          <Button type="primary" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </Modal>
  );
};

export default CustomImageCrop;
